/**
 * Create / edit a catalogue product.
 *
 * Mirrors the event add-on drawer (AddonsTab) deliberately — same TwDrawer shell, same
 * field rhythm, same footer buttons — so an organizer who knows one already knows the other.
 *
 * The on-site price set here applies ONLY to standalone QR sales. When this product is later
 * selected into an event as an add-on, the organizer sets a separate online price there; the
 * two channels are independent.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PlusIcon } from "@heroicons/react/20/solid";

import {
  TwButton,
  TwCheckBox,
  TwDropzone,
  TwInputGroup,
  TwTextarea,
} from "../../components/tailwind";
import TwDrawer from "../../components/tailwind/TwDrawer";
import PriceHelperText, { calculatePlatformServiceFee } from "../../components/PriceHelperText";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings } from "../../utils/confighelpers";
import { addProduct, updateProduct } from "../../utils/productsApi";

const emptyProduct = {
  name: "",
  description: "",
  photo: "",
  onSitePrice: "",
  isActive: true,
};

export default function AddEditProductDrawer({
  open,
  isEdit = false,
  product = {},
  handleClose = () => {},
  handleUpdate = () => {},
}) {
  const { t } = useTranslation();
  const userReduxState = useSelector(userState);

  const [values, setValues] = React.useState(emptyProduct);
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [addDesc, setAddDesc] = React.useState(false);
  const [addPhoto, setAddPhoto] = React.useState(false);

  // The platform fee on an on-site sale comes from the same bookzyPlatformFee config the
  // event add-on and ticket screens use, so the payout line here is computed the same way.
  const [bookzyPlatformFee, setBookzyPlatformFee] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const response = await axiosInstance({
          method: "get",
          url: `${getUrlForBookings()}payments/getAvailablePaymentMethods?organizerId=${userReduxState.userData?._id}&paymentMethodsFor=event&bookingFor=event`,
        });
        if (response?.data?.statusCode === 200 && response?.data?.data?.bookzyPlatformFee) {
          setBookzyPlatformFee(response.data.data.bookzyPlatformFee);
        }
      } catch (e) {
        // Non-fatal: without the fee config the payout line falls back to the full price,
        // which is still true for organizers who have no platform fee configured.
      }
    })();
  }, [open, userReduxState.userData?._id]);

  React.useEffect(() => {
    if (!open) return;
    setErrors({});

    if (isEdit && product?._id) {
      setValues({
        name: product.name || "",
        description: product.description || "",
        photo: product.photo || "",
        onSitePrice: product.onSitePrice !== undefined ? String(product.onSitePrice) : "",
        isActive: product.isActive !== false,
      });
      setAddDesc(!!product.description);
      setAddPhoto(!!product.photo);
    } else {
      setValues(emptyProduct);
      setAddDesc(false);
      setAddPhoto(false);
    }
  }, [open, isEdit, product]);

  const setField = (field, value) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  // Only meaningful when editing an existing product that may already have printed codes.
  const nameChanged =
    isEdit &&
    !!product?.name &&
    String(values.name || "").trim() !== "" &&
    String(values.name).trim() !== String(product.name).trim();

  const handleFormValueChange = (event) => {
    const { name, value } = event.target;
    setField(name, value);
  };

  const validate = () => {
    const next = {};
    if (!String(values.name || "").trim()) {
      next.name = t("errors.required", "This field is required");
    }
    const price = parseFloat(values.onSitePrice);
    if (isNaN(price) || price <= 0) {
      next.onSitePrice = t("products.priceRequired", "Enter a price greater than 0");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (saving || !validate()) return;
    setSaving(true);

    const payload = {
      name: String(values.name).trim(),
      description: String(values.description || "").trim(),
      photo: values.photo || "",
      onSitePrice: parseFloat(values.onSitePrice),
      isActive: values.isActive === true,
    };

    const result = isEdit
      ? await updateProduct({ _id: product._id, ...payload })
      : await addProduct(payload);

    setSaving(false);

    if (result.error) {
      setErrors({ form: result.message });
      return;
    }
    handleUpdate();
  };

  return (
    <TwDrawer
      title={`${isEdit ? t("update", "Update") : t("add", "Add")} ${t("products.product", "Product")}`}
      classNameDialogPanel={`!max-w-md`}
      open={open}
      onClose={handleClose}
      footerChildren={
        <>
          <TwButton
            variant={"secondary-outline"}
            className={"w-1/3"}
            onClick={handleClose}
          >
            {t("inputFieldsTitle.cancel", "Cancel")}
          </TwButton>
          <TwButton
            variant={"secondary"}
            className={"w-1/3 truncate"}
            disabled={saving}
            actionLoading={saving}
            onClick={handleSave}
          >
            {isEdit ? t("update", "Update") : t("add", "Add")}
          </TwButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TwInputGroup
          label={t("products.name", "Product name")}
          placeholder={t("products.namePlaceholder", "e.g. Cola Zero")}
          name={"name"}
          value={values.name}
          onChange={handleFormValueChange}
          errors={errors}
        />

        {/* Renaming never breaks a QR code — it encodes the product id, not the name. The
            only risk is a label printed earlier still showing the old name. We do NOT track
            whether anything was printed, so this is phrased conditionally rather than
            asserting a print happened. */}
        {nameChanged ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t(
              "products.renameWarning",
              "The QR code will keep working after renaming. If you have already printed it, the printed label will still show \"{{oldName}}\" — reprint it to avoid confusion at the counter.",
              { oldName: product?.name || "" }
            )}
          </div>
        ) : null}

        {addDesc === false ? (
          <button
            type="button"
            className="rounded inline-flex items-center w-max bg-white px-2 py-1 text-xs font-semibold text-gray-900 ring-0 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => setAddDesc(true)}
          >
            <PlusIcon className="size-5" />
            &nbsp;&nbsp;{t("myEvents.addDescription", "Add description")}
          </button>
        ) : (
          <TwTextarea
            label={t("myEvents.description", "Description")}
            name={"description"}
            value={values.description}
            row={3}
            onChange={handleFormValueChange}
            errors={errors}
          />
        )}

        {addPhoto ? (
          <TwDropzone
            uploadBtnText={
              values.photo
                ? t("myEvents.changeImage", "Change image")
                : t("myEvents.addImage", "Add image")
            }
            label={t("products.photo", "Product photo")}
            name={"photo"}
            errors={errors}
            value={values.photo || ""}
            onChange={(image) => setField("photo", image)}
          />
        ) : (
          <button
            type="button"
            className="rounded inline-flex items-center w-max bg-white px-2 py-1 text-xs font-semibold text-gray-900 ring-0 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={() => setAddPhoto(true)}
          >
            <PlusIcon className="size-5" />
            &nbsp;&nbsp;{t("myEvents.addPhoto", "Add photo")}
          </button>
        )}

        <TwInputGroup
          label={t("products.onSitePrice", "On-site price (€)")}
          placeholder={"0.00"}
          type={"number"}
          name={"onSitePrice"}
          value={values.onSitePrice}
          onChange={handleFormValueChange}
          errors={errors}
          helperText={
            <PriceHelperText
              t={t}
              amount={values.onSitePrice}
              serviceFee={calculatePlatformServiceFee(values.onSitePrice, bookzyPlatformFee)}
              // No toggle on products: the buyer pays exactly the listed price and the
              // platform fee always comes out of the organizer's side.
              absorbServiceFee={true}
              note={t(
                "products.onSitePriceHelper",
                "Price includes VAT. Used for QR sales at the venue. If you add this product to an event, you set the online price there."
              )}
            />
          }
        />

        {/* TwCheckBox rather than TwSwitch: it renders subTitle (TwSwitch silently drops it)
            and matches how AddonsTab presents availableForSale. */}
        <TwCheckBox
          title={t("products.available", "Available for sale")}
          subTitle={t(
            "products.availableHelper",
            "Turn off to pause QR sales without deleting the product. Printed codes will show it as unavailable."
          )}
          name={"isActive"}
          checked={values.isActive === true}
          onChange={(e) => setField("isActive", e.target.checked)}
          errors={errors}
        />

        {errors.form ? (
          <p className="text-sm text-red-600">{errors.form}</p>
        ) : null}
      </div>
    </TwDrawer>
  );
}
